import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { LoggedInGuard } from "../auth/auth.guards";
import { AccountPrerequisitesGuard } from "../prerequisites/account-prerequisites.guard";
import { MediaLibraryComponent } from "./media/media-library/media-library.component";
import { TemplateLibraryComponent } from "./template/template-library/template-library.component";
import { MediaService } from "./media.service";
import { map, take } from "rxjs/operators";
import { SessionService } from "../session.service";
import { SignalsService } from "../signals/signals.service";
import { TemplateComponent } from "./template/template/template.component";
import { from, Observable } from "rxjs";
import { PermissionService } from "../permission.service";
import { IntegrationsService } from "../integrations/integrations.service";
import { CropComponent } from "./crop/crop.component";

@Injectable({ providedIn: "root" })
export class MediasResolve implements Resolve<any> {
  constructor(
    private SessionService: SessionService,
    private MediaService: MediaService,
  ) {}

  resolve() {
    return this.MediaService.getMedias(
      this.SessionService.currentAccountValue!.accountID,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class TemplatesResolve implements Resolve<any> {
  constructor(
    private SessionService: SessionService,
    private SignalsService: SignalsService,
  ) {}

  resolve() {
    return this.SignalsService.getSignalTemplates(
      this.SessionService.currentAccountValue!.accountID,
      { page: 0, perPage: 999999999 },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class TemplateResolve implements Resolve<any> {
  constructor(
    private SessionService: SessionService,
    private SignalsService: SignalsService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.SignalsService.getSignalTemplate(
      this.SessionService.currentAccountValue!.accountID,
      route.params.templateID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class ViewResolve implements Resolve<any> {
  constructor() {}

  resolve() {
    return true;
  }
}

@Injectable({ providedIn: "root" })
export class ContentIntegrationsResolve implements Resolve<any> {
  constructor(
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.IntegrationsService.getIntegrations(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      false
    ).pipe(take(1));
  }
}

@Injectable({
  providedIn: "root",
})
export class TemplateListGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.templates.hasList()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class TemplateDetailGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.templates.hasDetail()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class TemplateWriteGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.templates.hasWrite()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
class MediaListGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.media.hasList()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard, AccountPrerequisitesGuard],
    data: { sidebar: "content" },
    children: [
      {
        path: "template",
        children: [
          {
            path: "library",
            component: TemplateLibraryComponent,
            data: { title: "Template Library" },
            canActivate: [TemplateListGuard],
            resolve: {
              // templates: TemplatesResolve,
            },
          },
          {
            path: "create",
            component: TemplateComponent,
            data: { title: "Create Template" },
            canActivate: [TemplateWriteGuard],
            resolve: {
              medias: MediasResolve,
            },
          },
          {
            path: "view/:templateID",
            component: TemplateComponent,
            data: { title: "View Template" },
            canActivate: [TemplateDetailGuard],
            resolve: {
              template: TemplateResolve,
              isView: ViewResolve,
            },
          },
          {
            path: "edit/:templateID",
            component: TemplateComponent,
            data: { title: "Edit Template" },
            canActivate: [TemplateWriteGuard],
            resolve: {
              medias: MediasResolve,
              template: TemplateResolve,
            },
          },
        ],
      },
      {
        path: "media",
        children: [
          {
            path: "library",
            component: MediaLibraryComponent,
            data: { title: "Media Library" },
            canActivate: [MediaListGuard],
            resolve: {
              // medias: MediasResolve,
              integrations: ContentIntegrationsResolve,
            },
          }
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class ContentRoutingModule {}

const contentModuleLazyLoadRoutes: Routes = [
  {
    path: "content",
    loadChildren: () => import("./content.module").then((m) => m.ContentModule),
  },
];

export { contentModuleLazyLoadRoutes };
